import type { CreateInvoiceLineItemInput } from 'payments-api/model';
import {
  useGetProductsSuspense,
  useGetUsersSuspense,
  usePostInvoices,
} from 'api/payments/generated/fragmentPaymentsAPI';
import { PaymentViewLayout } from 'components/Layout/PaymentViewLayout';
import { useToast } from 'components/ToastManager';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { makePath } from 'utils/routes';
import { useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';

import lilId from '@fragment/lil-id';
import { Toast } from '@fragment/ui/src/components/Toast';

import type { ItemTableItem } from './ItemTable';
import { getTotalsFromItems, InvoiceItemsSection } from './InvoiceItemsSection';
import { InvoiceLayout } from './InvoiceLayout';

type Props = {
  workspaceId: string;
};

export const CreateInvoicePageContent = ({
  workspaceId,
}: Props): JSX.Element => {
  useDocumentTitle('Create Invoice');

  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { data: productsData } = useGetProductsSuspense();
  const { data: usersData } = useGetUsersSuspense();
  const createInvoiceMutation = usePostInvoices();
  const [invoiceItems, setInvoiceItems] = useState<ItemTableItem[]>([]);
  const [, setLocation] = useLocation();
  const [selectedUserId, setSelectedUserId] = useState('all');
  // Form state managed by react-hook-form
  const [invoiceId, setInvoiceId] = useState('');

  const users = useMemo(
    () => [
      ...new Set(invoiceItems.map((item) => item.user_id).filter(Boolean)),
    ],
    [invoiceItems]
  );

  const handleAddInvoiceItem = useCallback(
    (item: Omit<ItemTableItem, 'id'>) => {
      setInvoiceItems((prev) => [
        ...prev,
        {
          ...item,
          id: lilId(),
        },
      ]);
    },
    []
  );

  const removeItem = useCallback((id: string) => {
    setInvoiceItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const handleUpdateInvoiceItem = useCallback(
    (id: string, item: Omit<ItemTableItem, 'id'>) => {
      setInvoiceItems((prev) =>
        prev.map((i) => (i.id === id ? { ...item, id } : i))
      );
    },
    []
  );

  const handleCreateInvoice = useCallback(async () => {
    try {
      // Map invoice items to API format
      const lineItems: CreateInvoiceLineItemInput[] = invoiceItems.map(
        (item) => {
          const { id, ...rest } = item;
          return rest;
        }
      );

      if (lineItems.length > 0) {
        await createInvoiceMutation.mutateAsync({
          data: {
            invoiceId,
            lineItems,
          },
        });
      }
      await queryClient.invalidateQueries({ queryKey: ['/invoices'] });

      // Redirect to invoices list page
      setLocation(
        makePath(['w', workspaceId, 'payments', 'invoices'], { absolute: true })
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to create invoice. Please try again.';
      showToast(
        <Toast
          type="error"
          message={`Failed to publish invoice: ${errorMessage}`}
        />
      );
    }
  }, [
    createInvoiceMutation,
    invoiceId,
    invoiceItems,
    workspaceId,
    setLocation,
    queryClient,
    showToast,
  ]);

  const handleBackToInvoices = useCallback(() => {
    setLocation(
      makePath(['w', workspaceId, 'payments', 'invoices'], { absolute: true })
    );
  }, [workspaceId, setLocation]);

  const totals = useMemo(
    () => getTotalsFromItems(invoiceItems),
    [invoiceItems]
  );

  const canPublish = useMemo(() => {
    if (!totals.isTotalBalanced) {
      return false;
    }
    if (invoiceId.trim() === '') {
      return false;
    }
    if (invoiceItems.length === 0) {
      return false;
    }

    return true;
  }, [invoiceId, invoiceItems, totals.isTotalBalanced]);

  return (
    <>
      <InvoiceLayout
        onBack={handleBackToInvoices}
        title={invoiceId}
        status="draft"
        primaryAction={{
          title: createInvoiceMutation.isPending ? 'Wait...' : 'Publish',
          'data-testid': 'publish-invoice-button',
          onClick: handleCreateInvoice,
          disabled: !canPublish || createInvoiceMutation.isPending,
          disabledTooltipText: createInvoiceMutation.isPending
            ? undefined
            : 'Please fill in all fields and add at least one item.',
        }}
        issued={new Date().toISOString()}
        setTitle={setInvoiceId}
        users={users}
        selectedUserId={selectedUserId}
        setSelectedUserId={setSelectedUserId}
      >
        <InvoiceItemsSection
          items={invoiceItems}
          products={productsData.data}
          users={usersData.data}
          selectedUserId={selectedUserId}
          onAddItem={handleAddInvoiceItem}
          onUpdateItem={handleUpdateInvoiceItem}
          onRemoveItem={removeItem}
        />
      </InvoiceLayout>
    </>
  );
};

export const CreateInvoicePage = ({ workspaceId }: Props): JSX.Element => (
  <Suspense
    fallback={
      <PaymentViewLayout title="Create New Invoice">
        <div className="text-main-500 text-medium">Loading...</div>
      </PaymentViewLayout>
    }
  >
    <CreateInvoicePageContent workspaceId={workspaceId} />
  </Suspense>
);
